import Tabs from "../../General/ResponsiveTabs/ResponsiveTabs";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  TextField,
  Drawer,
  Tab,
  Button,
  Typography,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import CandidateColumn from "./CandidateColumn/CandidateColumn";
import CommentsSection from "./CommentSection/CommentSection";
import UpdateCandidatePopup from "./UpdateCandidatePopup/UpdateCandidatePopup";
import { Candidate, CandidateComment } from "./types/CandidateBoardTypes";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { handleDownloadFile } from "../../../api/handleDownloadFile";
import ConfirmActionButton from "../../Fleet/ConfirmActionButton/ConfirmActionButton";
import { handleViewFile } from "../../../api/handleViewFile";
import ScheduleCandidateDialog from "./ScheduleCandidateDialog/ScheduleCandidateDialog";
import dayjs, { MOLDOVA_TIMEZONE } from "../../../utils/dayjsTimezone";
import type { Dayjs } from "dayjs";
import { getStoredEmployeeId } from "../../../utils/employeeIdStorage";

const statuses = ["NEW", "SCHEDULED", "HIRED", "REJECTED", "RESERVED"];

export const fetchCandidates = async (
  token: string | null | undefined,
  month: number,
  year: number,
  search?: string,
): Promise<Candidate[]> => {
  const params = new URLSearchParams();
  const normalizedSearch = search?.trim();

  if (normalizedSearch) {
    // Search is intentionally independent of the selected month. The API
    // searches the complete candidate history when only `search` is sent.
    params.append("search", normalizedSearch);
  } else {
    if (month) {
      params.append("month", month.toString());
    }
    if (year) {
      params.append("year", year.toString());
    }
  }

  const response = await fetch(
    `${apiVariable}/candidates${
      params.toString() ? `?${params.toString()}` : ""
    }`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error("Error fetching candidates");
  }
  return response.json();
};

const updateCandidateStatusAPI = async (
  candidate: Candidate,
  token: string | null | undefined,
) => {
  const response = await fetch(`${apiVariable}/candidate/status`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(candidate),
  });
  if (!response.ok) {
    throw new Error("Error updating candidate status");
  }
  return response.json();
};

const fetchCandidateComments = async (
  candidateId: string,
  token: string | null | undefined,
): Promise<CandidateComment[]> => {
  const response = await fetch(
    `${apiVariable}/candidate/${candidateId}/comments`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error("Error fetching comments");
  }
  return response.json();
};

const addCandidateComment = async (
  candidateId: string,
  comment: string,
  employeeId: string,
  token: string | null | undefined,
): Promise<CandidateComment> => {
  const params = new URLSearchParams({ employeeId });
  const response = await fetch(
    `${apiVariable}/candidate/${candidateId}/comments?${params.toString()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ candidateId, comment }),
    },
  );
  if (!response.ok) {
    throw new Error("Error adding comment");
  }
  return response.json();
};

const editCandidateComment = async (
  comment: CandidateComment & { candidateId: string },
  token: string | null | undefined,
) => {
  const response = await fetch(`${apiVariable}/candidate/comment`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(comment),
  });
  if (!response.ok) {
    throw new Error("Error editing comment");
  }
  return response.json();
};

const deleteCandidateComment = async (
  commentId: string,
  token: string | null | undefined,
) => {
  const response = await fetch(
    `${apiVariable}/candidate/comment/${commentId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error("Error deleting comment");
  }
};

const scheduleCandidateAPI = async (
  candidateId: string,
  scheduledFor: string | null,
  token: string | null | undefined,
): Promise<Candidate> => {
  const response = await fetch(
    `${apiVariable}/candidate/${candidateId}/schedule`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(scheduledFor),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Error scheduling candidate");
  }
  return response.json();
};

interface CandidateBoardProps {
  month: number;
  year: number;
}

const CandidateBoard: React.FC<CandidateBoardProps> = ({ month, year }) => {
  const mobile = useMediaQuery(useTheme().breakpoints.down("lg"));
  const [visibleStatus, setVisibleStatus] = useState(statuses[0]);
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [currentCandidate, setCurrentCandidate] = useState<Candidate | null>(
    null,
  );
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] =
    useState<Candidate | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [isDownloadingCv, setIsDownloadingCv] = useState(false);
  const [isViewingCv, setIsViewingCv] = useState(false);
  const normalizedSearchTerm = debouncedSearchTerm;

  useEffect(() => {
    const normalized = searchTerm.trim();

    if (!normalized) {
      setDebouncedSearchTerm("");
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(normalized);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const candidateQueryKey = useMemo(
    () => ["candidates", month, year, normalizedSearchTerm] as const,
    [month, normalizedSearchTerm, year],
  );

  const { data: candidates = [] } = useQuery({
    queryKey: candidateQueryKey,
    queryFn: () => fetchCandidates(token, month, year, normalizedSearchTerm),
    enabled: !!token,
  });

  const statusMutation = useMutation({
    mutationFn: (candidate: Candidate) =>
      updateCandidateStatusAPI(candidate, token),
    onMutate: async (updatedCandidate: Candidate) => {
      await queryClient.cancelQueries({ queryKey: candidateQueryKey });
      const previous = queryClient.getQueryData<Candidate[]>(candidateQueryKey);
      if (previous) {
        queryClient.setQueryData(
          candidateQueryKey,
          (old: Candidate[] | undefined) =>
            old?.map((cand) =>
              cand.id === updatedCandidate.id ? updatedCandidate : cand,
            ),
        );
      }
      return { previous };
    },
    onError: (_err, _cand, context) => {
      if (context?.previous) {
        queryClient.setQueryData(candidateQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: candidateQueryKey });
      queryClient.invalidateQueries({ queryKey: ["scheduledCandidates"] });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: ({
      candidateId,
      scheduledFor,
    }: {
      candidateId: string;
      scheduledFor: string | null;
    }) => scheduleCandidateAPI(candidateId, scheduledFor, token),
    onSuccess: (updatedCandidate) => {
      queryClient.setQueryData(
        candidateQueryKey,
        (old: Candidate[] | undefined) =>
          old?.map((cand) =>
            cand.id === updatedCandidate.id
              ? {
                  ...cand,
                  scheduledFor: updatedCandidate.scheduledFor,
                }
              : cand,
          ),
      );
      queryClient.invalidateQueries({ queryKey: ["scheduledCandidates"] });
    },
  });

  const updateCandidateStatus = useCallback(
    (id: string, newStatus: string, currentStatus: string) => {
      const candidate = candidates.find((c) => c.id === id);
      if (!candidate) {
        return;
      }

      if (newStatus === "SCHEDULED") {
        setCandidateToSchedule({ ...candidate, status: currentStatus });
        setScheduleDialogOpen(true);
        setScheduleError(null);
        return;
      }

      statusMutation.mutate({ ...candidate, status: newStatus });
    },
    [candidates, statusMutation],
  );

  const handleScheduleClose = useCallback(() => {
    setScheduleDialogOpen(false);
    setCandidateToSchedule(null);
    setScheduleError(null);
  }, []);

  const handleScheduleConfirm = useCallback(
    async (value: Dayjs) => {
      if (!candidateToSchedule) {
        return;
      }
      try {
        setScheduleError(null);
        const scheduledUtc = value.tz(MOLDOVA_TIMEZONE).utc().toISOString();
        await scheduleMutation.mutateAsync({
          candidateId: candidateToSchedule.id,
          scheduledFor: scheduledUtc,
        });
        await statusMutation.mutateAsync({
          ...candidateToSchedule,
          status: "SCHEDULED",
          scheduledFor: scheduledUtc,
        });
        handleScheduleClose();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to schedule candidate";
        setScheduleError(message);
      }
    },
    [
      candidateToSchedule,
      handleScheduleClose,
      scheduleMutation,
      statusMutation,
    ],
  );

  const filteredCandidates = useMemo(
    () =>
      candidates
        .filter((candidate) => {
          if (normalizedSearchTerm) {
            return true;
          }

          const appliedDate = dayjs(candidate.appliedDate);
          const scheduledDate = candidate.scheduledFor
            ? dayjs.utc(candidate.scheduledFor).tz(MOLDOVA_TIMEZONE)
            : null;
          const isInSelectedMonth = (date: Dayjs) =>
            date.isValid() &&
            date.month() + 1 === month &&
            date.year() === year;

          if (!appliedDate.isValid() && !scheduledDate?.isValid()) {
            return true;
          }

          return (
            isInSelectedMonth(appliedDate) ||
            (scheduledDate !== null && isInSelectedMonth(scheduledDate))
          );
        })
        .sort((firstCandidate, secondCandidate) => {
          const firstDate = dayjs(firstCandidate.appliedDate);
          const secondDate = dayjs(secondCandidate.appliedDate);

          const firstHasValidDate =
            Boolean(firstCandidate.appliedDate) && firstDate.isValid();
          const secondHasValidDate =
            Boolean(secondCandidate.appliedDate) && secondDate.isValid();

          if (!firstHasValidDate && !secondHasValidDate) {
            return 0;
          }

          if (!firstHasValidDate) {
            return 1;
          }

          if (!secondHasValidDate) {
            return -1;
          }

          return secondDate.valueOf() - firstDate.valueOf();
        }),
    [candidates, month, normalizedSearchTerm, year],
  );

  const { data: candidateComments = [] } = useQuery({
    queryKey: ["candidateComments", currentCandidate?.id],
    queryFn: () =>
      fetchCandidateComments(currentCandidate?.id as string, token),
    enabled: !!currentCandidate?.id,
  });

  const handleCandidateDoubleClick = useCallback((candidate: Candidate) => {
    setCurrentCandidate(candidate);
    setDrawerTab(0);
    setDrawerOpen(true);
  }, []);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setDrawerTab(newValue);
    },
    [],
  );

  const handleAddComment = useCallback(
    async (commentText: string) => {
      if (!currentCandidate) return;
      const employeeId = getStoredEmployeeId();
      await addCandidateComment(
        currentCandidate.id,
        commentText,
        employeeId,
        token,
      );
      queryClient.invalidateQueries({
        queryKey: ["candidateComments", currentCandidate.id],
      });
    },
    [currentCandidate, token, queryClient],
  );

  const handleEditComment = useCallback(
    async (updatedComment: CandidateComment) => {
      if (!currentCandidate) return;
      await editCandidateComment(
        { ...updatedComment, candidateId: currentCandidate.id },
        token,
      );
      queryClient.invalidateQueries({
        queryKey: ["candidateComments", currentCandidate.id],
      });
    },
    [currentCandidate, token, queryClient],
  );

  const handleDeleteComment = useCallback(
    async (id: string) => {
      if (!currentCandidate) return;
      await deleteCandidateComment(id, token);
      queryClient.invalidateQueries({
        queryKey: ["candidateComments", currentCandidate.id],
      });
    },
    [currentCandidate, token, queryClient],
  );

  useEffect(() => {
    if (
      currentCandidate &&
      !candidates.some((c) => c.id === currentCandidate.id)
    ) {
      setDrawerOpen(false);
      setCurrentCandidate(null);
    }
  }, [candidates, currentCandidate]);

  const handleDownloadCv = useCallback(async () => {
    if (!currentCandidate) return;
    setIsDownloadingCv(true);
    try {
      await handleDownloadFile(currentCandidate, "candidatecv", token);
    } finally {
      setIsDownloadingCv(false);
    }
  }, [currentCandidate, token]);

  const handleViewCv = useCallback(async () => {
    if (!currentCandidate) return;
    setIsViewingCv(true);
    try {
      await handleViewFile(currentCandidate, "candidatecv", token);
    } finally {
      setIsViewingCv(false);
    }
  }, [currentCandidate, token]);

  return (
    <Box>
      <TextField
        label="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      {mobile && (
        <TextField
          select
          fullWidth
          label="Candidate status"
          value={visibleStatus}
          onChange={(event) => setVisibleStatus(event.target.value)}
          sx={{ mb: 2 }}
        >
          {statuses.map((status) => (
            <MenuItem key={status} value={status}>
              {status} (
              {
                filteredCandidates.filter(
                  (candidate) => candidate.status === status,
                ).length
              }
              )
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box
        sx={{
          display: "flex",
          height: 800,
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        {(mobile ? [visibleStatus] : statuses).map((status) => (
          <CandidateColumn
            key={`${status}-${month}-${year}-${normalizedSearchTerm}`}
            status={status}
            candidates={filteredCandidates.filter((c) => c.status === status)}
            onDrop={updateCandidateStatus}
            onCandidateDoubleClick={handleCandidateDoubleClick}
          />
        ))}
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { top: 64, height: "calc(100% - 64px)" } }}
      >
        <Box sx={{ maxWidth: "100%", width: 400, p: 2 }}>
          <Button onClick={() => setDrawerOpen(false)}>
            Close candidate details
          </Button>
          <Tabs
            value={drawerTab}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="Candidate Info" />
            <Tab label="Comments" />
          </Tabs>
          {drawerTab === 0 && currentCandidate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">{`${currentCandidate.firstName} ${currentCandidate.lastName}`}</Typography>
              {mobile && (
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={
                    candidates.find(
                      (candidate) => candidate.id === currentCandidate.id,
                    )?.status ?? currentCandidate.status
                  }
                  sx={{ my: 2 }}
                  onChange={(event) => {
                    const candidate =
                      candidates.find(
                        (item) => item.id === currentCandidate.id,
                      ) ?? currentCandidate;
                    updateCandidateStatus(
                      candidate.id,
                      event.target.value,
                      candidate.status,
                    );
                  }}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Typography variant="body2">
                Position: {currentCandidate.position}
              </Typography>
              <Typography variant="body2">
                Email: {currentCandidate.email}
              </Typography>
              <Typography variant="body2">
                Phone: {currentCandidate.phoneNumber}
              </Typography>
              <Typography variant="body2">
                Applied Date:{" "}
                {dayjs(currentCandidate.appliedDate).format("MM/DD/YYYY")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                <UpdateCandidatePopup
                  candidate={currentCandidate}
                  onUpdated={(updated) => setCurrentCandidate(updated)}
                />
                <ConfirmActionButton
                  id={currentCandidate.id}
                  entityType="Candidate"
                  actionType="delete"
                  entityLabel="candidate"
                  queryKey="candidates"
                  buttonLabel="Delete"
                  buttonColor="error"
                />
                <Button
                  variant="outlined"
                  onClick={handleViewCv}
                  disabled={isViewingCv}
                  startIcon={
                    isViewingCv ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {isViewingCv ? "Viewing CV" : "View CV"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownloadCv}
                  disabled={isDownloadingCv}
                  startIcon={
                    isDownloadingCv ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {isDownloadingCv ? "Downloading CV" : "Download CV"}
                </Button>
              </Box>
            </Box>
          )}
          {drawerTab === 1 && (
            <Box sx={{ mt: 2, height: "100%" }}>
              <CommentsSection
                comments={candidateComments}
                handleAddComment={handleAddComment}
                handleEditComment={handleEditComment}
                handleDeleteComment={handleDeleteComment}
              />
            </Box>
          )}
        </Box>
      </Drawer>
      <ScheduleCandidateDialog
        open={scheduleDialogOpen}
        candidate={candidateToSchedule}
        onClose={handleScheduleClose}
        onSubmit={handleScheduleConfirm}
        isSubmitting={scheduleMutation.isPending || statusMutation.isPending}
        error={scheduleError ?? undefined}
      />
    </Box>
  );
};

export default CandidateBoard;
